import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';

import type { IPath } from '@feathr/blackbox';
import { Button, Icon, List, Spinner, Toolbar } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { useLicense } from '@feathr/extender/hooks';
import { useAccount, useLocalUrl, useStore } from '@feathr/extender/state';
import { logUserEvents } from '@feathr/hooks';

import Path from './Path';

function FlightAddPage(): JSX.Element {
  const account = useAccount();
  const { Flights, Paths } = useStore();
  const hasDefaultFlightPaths = useLicense('hasDefaultFlightPaths');
  const history = useHistory();
  const localUrl = useLocalUrl();
  const { eventId } = useParams<{ eventId: string }>();
  const { t } = useTranslation();

  const flights = Flights.list({
    filters: {
      shared: true,
    },
  });

  const paths = hasDefaultFlightPaths ? Paths.list() : { isPending: false, models: [] };

  const chooseFlightPath = useCallback(
    async (selectedPath: IPath) => {
      const flight = await Paths.createFlightFromPath(
        selectedPath.id,
        {
          event_id: eventId,
        },
        Flights,
      );

      logUserEvents({
        'Created flight': { account_id: account.id, flight_id: flight.id },
        [`Created ${selectedPath.shared_name} flight path`]: {
          account_id: account.id,
          flight_id: flight.id,
        },
      });
      history.push(localUrl(flight.getItemUrl()));
    },
    [Flights, Paths, account.id, eventId, history, localUrl],
  );

  async function handleOnClick(): Promise<void> {
    await chooseFlightPath(Paths.get('custom').toJS());
  }

  const actions = (
    <Toolbar>
      <Button onClick={handleOnClick} prefix={<Icon.Add />} type={'primary'}>
        {t('New flight path')}
      </Button>
    </Toolbar>
  );

  const explanation = t(
    'Flight Paths are reusable sets of campaigns with each leg focused on a specific objective.',
  );
  const descriptionWithDefaults = t(
    'Select one of our suggested flight paths to get started, or add a new flight to build your own.',
  );
  const descriptionWithoutDefaults = t('Add a new flight path to get started.');
  const description = hasDefaultFlightPaths ? descriptionWithDefaults : descriptionWithoutDefaults;

  function renderFlights(): JSX.Element | JSX.Element[] | string {
    if (flights.isPending) {
      return <Spinner />;
    }

    if (flights.models.length > 0) {
      return flights.models.map((flight) => (
        <Path
          choosePath={chooseFlightPath}
          key={flight.id}
          path={flight.toJS() as unknown as IPath}
        />
      ));
    }

    return t('No flight paths yet');
  }

  return (
    <Page
      actions={actions}
      collapseHeader={false}
      collapseHeaderOnScroll={true}
      description={
        <>
          {explanation}
          <br />
          {description}
        </>
      }
      title={'Flight Paths'}
    >
      <List>
        {!paths.isPending && paths.models.length > 0
          ? paths.models.map((path) => (
              <Path choosePath={chooseFlightPath} key={path.id} path={path.toJS()} />
            ))
          : null}
        {renderFlights()}
      </List>
    </Page>
  );
}

export default observer(FlightAddPage);
